import { useEffect, useState } from "react";
import { Loader2, Plus, Truck, CheckCircle, Ban } from "lucide-react";
import { adminListTransportCompanies, adminCreateTransportCompany, adminUpdateTransportStatus } from "@/Calls/callTransportAuth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

interface Company {
    id: number; company_name: string; company_code: string; email: string;
    phone: string | null; mc_number: string | null; dot_number: string | null;
    status: string; contact_name: string | null; shipment_count: number;
}

function StatusBadge({ status }: { status: string }) {
    if (status === "active") return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>;
    if (status === "suspended") return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Suspended</Badge>;
    return <Badge variant="outline">Pending</Badge>;
}

const BLANK = {
    company_name: "", company_code: "", email: "", password: "",
    phone: "", contact_name: "", mc_number: "", dot_number: "",
    operating_states: "", notes: "",
};

export default function AdminTransportPage() {
    const [companies,  setCompanies]  = useState<Company[]>([]);
    const [loading,    setLoading]    = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form,       setForm]       = useState(BLANK);
    const [submitting, setSubmitting] = useState(false);
    const [formError,  setFormError]  = useState<string | null>(null);

    const load = () => {
        setLoading(true);
        adminListTransportCompanies()
            .then((d) => setCompanies(d.companies ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true); setFormError(null);
        try {
            const res = await adminCreateTransportCompany(form);
            if (!res.ok) throw new Error(res.message);
            setShowCreate(false); setForm(BLANK); load();
        } catch (e: any) { setFormError(e.message); }
        finally { setSubmitting(false); }
    };

    const handleStatus = async (id: number, status: string) => {
        await adminUpdateTransportStatus(id, status); load();
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    return (
        <div className="space-y-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-light uppercase tracking-wide">Transport Companies</h1>
                    <p className="text-muted-foreground text-sm mt-1">{companies.length} registered carriers</p>
                </div>
                <Button className="bg-gradient-to-r from-blue-600 to-sky-600 text-white" onClick={() => setShowCreate(true)}>
                    <Plus className="h-4 w-4 mr-2" />Add Company
                </Button>
            </div>

            {showCreate && (
                <Card className="p-6 border-2 border-blue-200">
                    <h3 className="font-semibold mb-4">Add Transport Company</h3>
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                            {[
                                { label: "Company Name *",    key: "company_name",    req: true },
                                { label: "Company Code *",    key: "company_code",    req: true, placeholder: "e.g. ABC_TRANS" },
                                { label: "Email *",           key: "email",           req: true, type: "email" },
                                { label: "Password *",        key: "password",        req: true, type: "password" },
                                { label: "Contact Name",      key: "contact_name" },
                                { label: "Phone",             key: "phone" },
                                { label: "MC Number",         key: "mc_number" },
                                { label: "DOT Number",        key: "dot_number" },
                                { label: "Operating States",  key: "operating_states", placeholder: "FL,GA,AL" },
                            ].map(({ label, key, req, type, placeholder }) => (
                                <div key={key}>
                                    <label className="text-sm font-medium">{label}</label>
                                    <Input
                                        required={req} type={type ?? "text"}
                                        value={(form as any)[key]} placeholder={placeholder}
                                        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                                        className="mt-1"
                                    />
                                </div>
                            ))}
                        </div>
                        {formError && <p className="text-red-500 text-sm">{formError}</p>}
                        <div className="flex gap-3">
                            <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                            <Button type="submit" className="bg-blue-600 text-white" disabled={submitting}>
                                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create Company
                            </Button>
                        </div>
                    </form>
                </Card>
            )}

            <Card>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b">
                            <tr className="text-left">
                                <th className="p-4 font-medium">Company</th>
                                <th className="p-4 font-medium">MC # / DOT #</th>
                                <th className="p-4 font-medium">Status</th>
                                <th className="p-4 font-medium">Shipments</th>
                                <th className="p-4 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {companies.length === 0 ? (
                                <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No companies yet</td></tr>
                            ) : companies.map((c) => (
                                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30">
                                    <td className="p-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                                                <Truck className="h-4 w-4 text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="font-medium">{c.company_name}</p>
                                                <p className="text-xs text-muted-foreground">{c.email}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4 text-muted-foreground text-xs">
                                        {c.mc_number && <div>MC: {c.mc_number}</div>}
                                        {c.dot_number && <div>DOT: {c.dot_number}</div>}
                                        {!c.mc_number && !c.dot_number && "—"}
                                    </td>
                                    <td className="p-4"><StatusBadge status={c.status} /></td>
                                    <td className="p-4">{c.shipment_count ?? 0}</td>
                                    <td className="p-4">
                                        <div className="flex gap-2">
                                            {c.status !== "active" && (
                                                <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleStatus(c.id, "active")}>
                                                    <CheckCircle className="h-3 w-3 mr-1" />Approve
                                                </Button>
                                            )}
                                            {c.status === "active" && (
                                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleStatus(c.id, "suspended")}>
                                                    <Ban className="h-3 w-3 mr-1" />Suspend
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}
